import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartCard } from './ChartCard'
import { EmptyState } from './StatusIndicator'
import { formatBytes, formatBytesPerSec, deriveRateSeries } from './format'
import type { NetworkInterfaceRow, SeriesPoint } from '../../types/monitoring'

// Normalizes an address field into a clean string[] no matter what
// shape it actually arrives in:
//   - a real array                      -> used as-is
//   - a JSON-encoded string ("[...]")   -> parsed once
//   - a JSON-encoded string of a        -> parsed twice (defends
//     JSON-encoded string ("\"[...]\"")    against double-encoding
//                                           anywhere upstream)
//   - anything else / unparseable       -> [] (never spread blindly,
//                                           which is what produced
//                                           the character-by-character
//                                           garbling seen earlier)
function normalizeAddresses(value: unknown): string[] {
  let v = value
  for (let attempt = 0; attempt < 2; attempt++) {
    if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string')
    if (typeof v !== 'string') return []
    const trimmed = v.trim()
    // A single-encoded array starts with `[`. A *double*-encoded one
    // starts with `"` (JSON.stringify wraps the whole string in
    // quotes) — accept either so one more parse pass can unwrap it.
    if (!trimmed.startsWith('[') && !trimmed.startsWith('"')) return []
    try {
      v = JSON.parse(trimmed)
    } catch {
      return []
    }
  }
  return Array.isArray(v) ? (v as unknown[]).filter((x): x is string => typeof x === 'string') : []
}

export function NetworkPanel({ interfaces, throughputSeries, loading, error }: {
  interfaces: NetworkInterfaceRow[]; throughputSeries: SeriesPoint[]; loading: boolean; error?: string
}) {
  const rxRate = deriveRateSeries(throughputSeries, 'bytes_recv')
  const txRate = deriveRateSeries(throughputSeries, 'bytes_sent')
  const merged = rxRate.map((r, i) => ({ time: r.time, rx: r.value, tx: txRate[i]?.value ?? 0 }))

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard title="Network Throughput" subtitle="Received / sent bytes per second" loading={loading} error={error} isEmpty={!merged.length}>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={merged} margin={{ left: -20, right: 8 }}>
            <defs>
              <linearGradient id="netRxGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0284c7" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#0284c7" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="netTxGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
              tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
            <YAxis tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} tickFormatter={(v) => formatBytes(v)} />
            <Tooltip formatter={(v: number, name: string) => [formatBytesPerSec(v), name === 'rx' ? 'Received' : 'Sent']}
              labelFormatter={(l) => new Date(l).toLocaleString()} />
            <Area type="monotone" dataKey="rx" stroke="#0284c7" strokeWidth={2} fill="url(#netRxGradient)" isAnimationActive={false} />
            <Area type="monotone" dataKey="tx" stroke="#7c3aed" strokeWidth={2} fill="url(#netTxGradient)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
        <h2 className="font-bold text-secondary mb-4">Network Interfaces</h2>
        {loading ? (
          <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
        ) : !interfaces.length ? (
          <EmptyState />
        ) : (
          <div className="space-y-2.5 max-h-56 overflow-y-auto">
            {interfaces.map((iface) => {
              const v4 = normalizeAddresses(iface.ipv4_addresses)
              const v6 = normalizeAddresses(iface.ipv6_addresses)
              const addressList = (v4.length || v6.length) ? [...v4, ...v6] : normalizeAddresses(iface.addresses)
              return (
              <div key={iface.name} className="flex items-center justify-between py-2 px-3 rounded-xl bg-neutral-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${iface.is_up ? 'bg-emerald-500' : 'bg-neutral-300'}`} />
                    <span className="font-semibold text-neutral-800 text-sm truncate">{iface.name}</span>
                    {iface.if_type && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-neutral-200 text-neutral-600 shrink-0">
                        {iface.if_type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-400 truncate">{addressList.join(', ') || 'no address'}</p>
                  {(iface.mac_address || iface.mtu) && (
                    <p className="text-[10px] text-neutral-300 truncate">
                      {iface.mac_address ?? ''}{iface.mac_address && iface.mtu ? ' · ' : ''}{iface.mtu ? `MTU ${iface.mtu}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-right text-xs text-neutral-500 shrink-0 ml-3">
                  <div>↓ {formatBytes(iface.bytes_recv)}</div>
                  <div>↑ {formatBytes(iface.bytes_sent)}</div>
                </div>
              </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}



// import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
// import { ChartCard } from './ChartCard'
// import { EmptyState } from './StatusIndicator'
// import { formatBytes, formatBytesPerSec, deriveRateSeries } from './format'
// import type { NetworkInterfaceRow, SeriesPoint } from '../../types/monitoring'

// export function NetworkPanel({ interfaces, throughputSeries, loading, error }: {
//   interfaces: NetworkInterfaceRow[]; throughputSeries: SeriesPoint[]; loading: boolean; error?: string
// }) {
//   const rxRate = deriveRateSeries(throughputSeries, 'bytes_recv')
//   const txRate = deriveRateSeries(throughputSeries, 'bytes_sent')
//   const merged = rxRate.map((r, i) => ({ time: r.time, rx: r.value, tx: txRate[i]?.value ?? 0 }))

//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//       <ChartCard title="Network Throughput" subtitle="Received / sent bytes per second" loading={loading} error={error} isEmpty={!merged.length}>
//         <ResponsiveContainer width="100%" height={220}>
//           <AreaChart data={merged} margin={{ left: -20, right: 8 }}>
//             <defs>
//               <linearGradient id="netRxGradient" x1="0" y1="0" x2="0" y2="1">
//                 <stop offset="0%" stopColor="#0284c7" stopOpacity={0.35} />
//                 <stop offset="100%" stopColor="#0284c7" stopOpacity={0} />
//               </linearGradient>
//               <linearGradient id="netTxGradient" x1="0" y1="0" x2="0" y2="1">
//                 <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.3} />
//                 <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
//               </linearGradient>
//             </defs>
//             <CartesianGrid vertical={false} stroke="#f0f0f0" />
//             <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
//               tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
//             <YAxis tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} tickFormatter={(v) => formatBytes(v)} />
//             <Tooltip formatter={(v: number, name: string) => [formatBytesPerSec(v), name === 'rx' ? 'Received' : 'Sent']}
//               labelFormatter={(l) => new Date(l).toLocaleString()} />
//             <Area type="monotone" dataKey="rx" stroke="#0284c7" strokeWidth={2} fill="url(#netRxGradient)" isAnimationActive={false} />
//             <Area type="monotone" dataKey="tx" stroke="#7c3aed" strokeWidth={2} fill="url(#netTxGradient)" isAnimationActive={false} />
//           </AreaChart>
//         </ResponsiveContainer>
//       </ChartCard>

//       <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
//         <h2 className="font-bold text-secondary mb-4">Network Interfaces</h2>
//         {loading ? (
//           <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
//         ) : !interfaces.length ? (
//           <EmptyState />
//         ) : (
//           <div className="space-y-2.5 max-h-56 overflow-y-auto">
//             {interfaces.map((iface) => (
//               <div key={iface.name} className="flex items-center justify-between py-2 px-3 rounded-xl bg-neutral-50">
//                 <div className="min-w-0">
//                   <div className="flex items-center gap-2">
//                     <span className={`w-2 h-2 rounded-full shrink-0 ${iface.is_up ? 'bg-emerald-500' : 'bg-neutral-300'}`} />
//                     <span className="font-semibold text-neutral-800 text-sm truncate">{iface.name}</span>
//                   </div>
//                   <p className="text-xs text-neutral-400 truncate">{(iface.addresses ?? []).join(', ') || 'no address'}</p>
//                 </div>
//                 <div className="text-right text-xs text-neutral-500 shrink-0 ml-3">
//                   <div>↓ {formatBytes(iface.bytes_recv)}</div>
//                   <div>↑ {formatBytes(iface.bytes_sent)}</div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }